import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDeviceAuditLog, type DeviceAuditEntry } from '../../services/adminApi';

interface Props {
  siteId: string;
  deviceId: string | null;
}

export function DeviceRatingsAuditLog({ siteId, deviceId }: Props) {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['device-audit-log', siteId, deviceId, page],
    queryFn: () => fetchDeviceAuditLog(siteId, deviceId!, { page, limit: 10 }),
    enabled: !!deviceId,
  });

  if (!deviceId) {
    return (
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-4">
        <h2 className="text-sm font-semibold text-ems-text-primary mb-2">Change history</h2>
        <div className="text-ems-text-muted text-xs">
          Click the History icon on a device row to see its edit history.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-4">
      <h2 className="text-sm font-semibold text-ems-text-primary mb-3">
        Change history · <span className="font-mono text-ems-text-muted">{deviceId}</span>
      </h2>
      {isLoading && <div className="text-ems-text-muted text-sm">Loading...</div>}
      {isError && <div className="text-ems-red text-sm">Failed to load audit log.</div>}
      {data && data.entries.length === 0 && (
        <div className="text-ems-text-muted text-sm">No changes yet for this device.</div>
      )}
      <ul className="space-y-2">
        {data?.entries.map((entry) => (
          <li key={entry.log_id} className="text-sm border border-ems-border rounded-lg p-3">
            <div className="flex justify-between gap-2">
              <span className="text-ems-text-primary truncate">{entry.actor_email}</span>
              <span className="text-ems-text-muted text-xs whitespace-nowrap">
                {new Date(entry.created_at).toLocaleString()}
              </span>
            </div>
            <div className="text-ems-text-muted text-xs mt-1">
              Changed: {entry.details.changed_fields.join(', ')}
            </div>
            <button
              type="button"
              onClick={() =>
                setExpanded(expanded === entry.log_id ? null : entry.log_id)
              }
              className="text-ems-green text-xs mt-1 hover:underline"
            >
              {expanded === entry.log_id ? 'Hide diff' : 'Show diff'}
            </button>
            {expanded === entry.log_id && <DiffView entry={entry} />}
          </li>
        ))}
      </ul>
      {data && data.pagination.total_pages > 1 && (
        <div className="flex justify-between items-center mt-3 text-xs text-ems-text-muted">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="disabled:opacity-50 hover:text-ems-green"
          >
            Prev
          </button>
          <span>
            Page {page} of {data.pagination.total_pages}
          </span>
          <button
            type="button"
            disabled={page >= data.pagination.total_pages}
            onClick={() => setPage((p) => p + 1)}
            className="disabled:opacity-50 hover:text-ems-green"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function DiffView({ entry }: { entry: DeviceAuditEntry }) {
  return (
    <div className="mt-2 space-y-1 text-xs">
      {entry.details.changed_fields.map((f) => (
        <div key={f} className="grid grid-cols-[auto_1fr_1fr] gap-2">
          <span className="font-mono text-ems-text-muted">{f}:</span>
          <span className="text-ems-red/80 line-through break-all">
            {JSON.stringify(entry.details.old_values[f])}
          </span>
          <span className="text-ems-green break-all">
            {JSON.stringify(entry.details.new_values[f])}
          </span>
        </div>
      ))}
    </div>
  );
}
